/**
 * StoryPointsManager — dense narrative markers along a track.
 *
 * Unlike POIs (landmark-level, 200-500m spacing), Story Points are
 * tight (~10m spacing) scenes anchored to specific GPS coordinates.
 * Together they form a continuous narrated walk — one scene every
 * ~10 seconds of walking, each building on the last.
 *
 * Story Points are loaded from the library's IACP response layer
 * (via the [TRACK:N] tag that triggers server-side artrack lookups).
 * This manager handles the client-side trigger logic: distance
 * calculation, auto-play decisions, single-active-at-a-time mutex
 * via AudioBusManager, and persistent heard-tracking.
 */
const STORY_STATE_SCHEMA_VERSION = 1;
const DEFAULT_TRIGGER_RADIUS_M = 15;
const DEFAULT_TTS_LANG = 'de-AT';
const DEFAULT_TTS_RATE = 0.92;
export class StoryPointsManager {
    constructor(bus, localState, audioBus) {
        this._config = null;
        this._storyPoints = [];
        this._current = null;
        this._inRange = [];
        this._next = null;
        this._nextDistance = null;
        this._heard = new Set();
        this._isSpeaking = false;
        this._listeners = new Set();
        this._unsubscribeLocation = null;
        this._currentUtterance = null;
        this._lastUserLat = null;
        this._lastUserLng = null;
        this._bus = bus;
        this._localState = localState;
        this._audioBus = audioBus;
        this._audioBus.register({
            id: StoryPointsManager.SOURCE_ID,
            onPause: () => this._pauseSpeechExternally(),
        }, 'story-points');
    }
    /**
     * Activate a story set. The story points themselves come from
     * loadStoryPoints() — this just binds the consumer's config and
     * restores the heard-set from localStorage.
     */
    activate(config) {
        this.deactivate();
        this._config = config;
        if (!config.storyId)
            return;
        // Restore heard set from persisted state
        const stored = this._localState.load('story', config.storyId, STORY_STATE_SCHEMA_VERSION);
        if (stored) {
            this._heard = new Set(stored.heardPointIds);
        }
        else {
            this._heard = new Set();
            this._persist();
        }
        // Subscribe to location updates
        const handler = (snap) => {
            if (snap.lat !== null && snap.lon !== null) {
                this._onLocationUpdate(snap.lat, snap.lon);
            }
        };
        this._bus.on('location:changed', handler);
        this._unsubscribeLocation = () => this._bus.off('location:changed', handler);
        this._notify();
    }
    /** Deactivate the current story. Releases audio bus and clears state. */
    deactivate() {
        if (this._unsubscribeLocation) {
            this._unsubscribeLocation();
            this._unsubscribeLocation = null;
        }
        if (this._currentUtterance) {
            window.speechSynthesis?.cancel();
            this._currentUtterance = null;
        }
        this._audioBus.release(StoryPointsManager.SOURCE_ID);
        this._isSpeaking = false;
        this._current = null;
        this._inRange = [];
        this._next = null;
        this._nextDistance = null;
        this._config = null;
        this._storyPoints = [];
        this._heard = new Set();
        this._notify();
    }
    /**
     * Load the story-point dataset for the currently-active story.
     * Called by AudioGuide when the IACP response includes story
     * points (i.e. when [TRACK:N] triggered a curated lookup server-side).
     */
    loadStoryPoints(points) {
        if (!this._config?.storyId)
            return;
        this._storyPoints = points.filter((p) => p.storyId === this._config.storyId);
        // Re-evaluate trigger state with the new point list
        if (this._lastUserLat !== null && this._lastUserLng !== null) {
            this._onLocationUpdate(this._lastUserLat, this._lastUserLng);
        }
        else {
            this._notify();
        }
    }
    /** Manually trigger a story point (e.g. "play missed story" button). */
    playPoint(pointId) {
        const point = this._storyPoints.find((p) => p.id === pointId);
        if (!point)
            return;
        void this._speak(point);
    }
    /** Skip the currently playing story point. */
    skipCurrent() {
        if (this._currentUtterance) {
            window.speechSynthesis?.cancel();
        }
        this._current = null;
        this._isSpeaking = false;
        this._audioBus.release(StoryPointsManager.SOURCE_ID);
        this._notify();
    }
    /** Clear the heard history for the current story set. */
    resetHistory() {
        if (!this._config?.storyId)
            return;
        this._heard = new Set();
        this._localState.remove('story', this._config.storyId);
        this._persist();
        this._notify();
    }
    /** Register a state-change listener (used by the React hook). */
    onStateChange(listener) {
        this._listeners.add(listener);
        listener(this.getState());
        return () => {
            this._listeners.delete(listener);
        };
    }
    /** Current immutable state snapshot. */
    getState() {
        const total = this._storyPoints.length;
        const heardCount = this._storyPoints.filter((p) => this._heard.has(p.id)).length;
        return {
            storyPoints: [...this._storyPoints],
            current: this._current,
            inRange: [...this._inRange],
            next: this._next,
            nextDistance: this._nextDistance,
            heard: new Set(this._heard),
            progress: {
                heard: heardCount,
                total,
                percent: total > 0 ? Math.round((heardCount / total) * 100) : 0,
            },
            isActive: this._config?.storyId !== null && this._config?.storyId !== undefined,
            isSpeaking: this._isSpeaking,
        };
    }
    // ──────────────────────────────────────────────────────────────
    // Private helpers
    // ──────────────────────────────────────────────────────────────
    _onLocationUpdate(lat, lon) {
        this._lastUserLat = lat;
        this._lastUserLng = lon;
        if (!this._config || this._storyPoints.length === 0)
            return;
        const triggerRadius = this._config.triggerRadius ?? DEFAULT_TRIGGER_RADIUS_M;
        const autoPlay = this._config.autoPlay !== false;
        const withDistance = this._storyPoints.map((p) => ({
            point: p,
            distance: StoryPointsManager._haversineMeters(lat, lon, p.lat, p.lng),
        }));
        withDistance.sort((a, b) => a.distance - b.distance);
        this._inRange = withDistance
            .filter((entry) => entry.distance <= triggerRadius)
            .map((entry) => entry.point);
        const nextUnheard = withDistance.find((entry) => !this._heard.has(entry.point.id));
        if (nextUnheard) {
            this._next = nextUnheard.point;
            this._nextDistance = Math.round(nextUnheard.distance);
        }
        else {
            this._next = null;
            this._nextDistance = null;
        }
        // Auto-play: if any in-range point is unheard AND autoPlay is on AND
        // we're not currently speaking, start the closest unheard one.
        if (autoPlay && !this._isSpeaking) {
            const closestUnheardInRange = withDistance.find((entry) => entry.distance <= triggerRadius && !this._heard.has(entry.point.id));
            if (closestUnheardInRange) {
                void this._speak(closestUnheardInRange.point);
            }
        }
        this._notify();
    }
    async _speak(point) {
        if (typeof window === 'undefined' || !window.speechSynthesis)
            return;
        if (!this._audioBus.request(StoryPointsManager.SOURCE_ID)) {
            return;
        }
        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(point.text);
            utterance.lang = this._config?.ttsLang ?? DEFAULT_TTS_LANG;
            utterance.rate = this._config?.ttsRate ?? DEFAULT_TTS_RATE;
            this._currentUtterance = utterance;
            this._current = point;
            this._isSpeaking = true;
            this._notify();
            utterance.onend = () => {
                // Mark as heard and persist
                this._heard = new Set([...this._heard, point.id]);
                this._persist();
                this._isSpeaking = false;
                this._current = null;
                this._currentUtterance = null;
                this._audioBus.release(StoryPointsManager.SOURCE_ID);
                this._notify();
            };
            utterance.onerror = () => {
                this._isSpeaking = false;
                this._current = null;
                this._currentUtterance = null;
                this._audioBus.release(StoryPointsManager.SOURCE_ID);
                this._notify();
            };
            window.speechSynthesis.speak(utterance);
        }
        catch {
            this._isSpeaking = false;
            this._audioBus.release(StoryPointsManager.SOURCE_ID);
        }
    }
    /** Called by AudioBusManager when a higher-priority source preempts us. */
    _pauseSpeechExternally() {
        if (typeof window !== 'undefined' && window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
        this._isSpeaking = false;
        this._current = null;
        this._currentUtterance = null;
        this._notify();
    }
    _persist() {
        if (!this._config?.storyId)
            return;
        const state = {
            schema_version: STORY_STATE_SCHEMA_VERSION,
            heardPointIds: Array.from(this._heard),
            lastHeardAt: Date.now(),
            startedAt: Date.now(),
        };
        this._localState.save('story', this._config.storyId, state);
    }
    _notify() {
        const state = this.getState();
        for (const listener of this._listeners)
            listener(state);
    }
    static _haversineMeters(lat1, lon1, lat2, lon2) {
        const R = 6371000;
        const toRad = (d) => (d * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLon = toRad(lon2 - lon1);
        const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }
}
StoryPointsManager.SOURCE_ID = 'audio-guide:story-points';
//# sourceMappingURL=StoryPointsManager.js.map